import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  AlertTriangle, ArrowRight, ArrowUpRight, BadgeCheck, CalendarClock, CreditCard, Gift, Globe2, Layers, MessageSquareReply, RefreshCw, Send, ShieldCheck, Smartphone,
} from 'lucide-react';
import { WsSegmented } from 'src/stage/WsControls';
import HelpTip from 'src/stage/HelpTip';
import { Skeleton, Status, formatNumber, relativeTime } from 'src/stage/StageUi';
import { qualityOf, tierLabel } from 'src/features/Dashboard/homeModel';
import {
  BILLING_HUB_URL, SERVICE_FREE_TIER, formatMoney, marketForIso, pricingCategoryColor, pricingCategoryLabel,
} from './pricingModel';

const RANGES = [7, 30, 90].map((value) => ({ id: value, label: `${value} days` }));

const monthName = (iso) => {
  const date = iso ? new Date(iso) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long' }) : 'next month';
};
const dayLabel = (value) => {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// Old backends answer with "conversations"; turn that into the per-message shape.
function pricingOf(data) {
  if (data?.pricing) return data.pricing;
  const legacy = data?.conversations;
  if (!legacy) return null;
  const byCategory = Object.fromEntries(Object.entries(legacy.by_category || {}).map(([key, row]) => [key, { volume: row.conversations, charged: row.cost > 0 ? row.conversations : 0, free: row.cost > 0 ? 0 : row.conversations, cost: row.cost }]));
  return { total_volume: legacy.total_conversations, charged_volume: null, free_volume: null, estimated_cost: legacy.estimated_cost, by_category: byCategory, by_country: {}, daily: [], tiers: [], legacy: true };
}

function Money({ value, currency }) {
  if (value === null || value === undefined) return '—';
  return currency ? formatMoney(value, currency) : `≈ ${Number(value).toFixed(2)}`;
}

function CostChart({ daily, currency }) {
  const data = useMemo(() => daily.map((point) => ({ day: point.day, cost: Number(point.cost) || 0, charged: point.charged || 0 })), [daily]);
  if (!data.length) return <p className="ws-muted mb-empty-line">No charges in this period.</p>;
  return (
    <div className="mb-chart" role="img" aria-label={`Meta charges per day, ${data.length} days`}>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 8, right: 4, left: -8, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="rgba(16,47,69,.07)" />
          <XAxis dataKey="day" tickFormatter={dayLabel} tick={{ fontSize: 11, fill: '#6B8494' }} axisLine={false} tickLine={false} minTickGap={18} />
          <YAxis tick={{ fontSize: 11, fill: '#6B8494' }} axisLine={false} tickLine={false} width={48} tickFormatter={(value) => (currency ? formatMoney(value, currency) : value)} />
          <Tooltip cursor={{ fill: 'rgba(20,125,181,.06)' }} content={({ active, payload, label }) => (active && payload?.length ? (
            <div className="wa-tooltip">
              <strong>{dayLabel(label)}</strong>
              <span><i style={{ background: '#147DB5' }} aria-hidden="true" />Charges<b className="ws-num"><Money value={payload[0].value} currency={currency} /></b></span>
              <span><i style={{ background: '#8CA1B2' }} aria-hidden="true" />Charged messages<b className="ws-num">{formatNumber(payload[0].payload.charged)}</b></span>
            </div>
          ) : null)} />
          <Bar dataKey="cost" fill="#147DB5" radius={[6, 6, 2, 2]} maxBarSize={26} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function CategoryBreakdown({ pricing, currency }) {
  const rows = Object.entries(pricing.by_category || {})
    .map(([key, row]) => ({ key, ...row }))
    .sort((a, b) => (b.cost - a.cost) || (b.volume - a.volume));
  const maxCost = Math.max(...rows.map((row) => row.cost), 0);
  if (!rows.length) return <p className="ws-muted mb-empty-line">No messages delivered in this period.</p>;
  return (
    <ul className="mb-cats">
      {rows.map((row) => (
        <li key={row.key}>
          <span className="mb-cat-dot" style={{ background: pricingCategoryColor(row.key) }} aria-hidden="true" />
          <span className="mb-cat-name">
            <strong>{pricingCategoryLabel(row.key)}</strong>
            <small>
              {formatNumber(row.volume)} delivered
              {row.free > 0 && row.charged > 0 && ` · ${formatNumber(row.free)} free`}
              {row.charged === 0 && row.volume > 0 && ' · all free'}
            </small>
          </span>
          <span className="mb-cat-bar" aria-hidden="true"><i style={{ width: `${maxCost ? Math.max(3, (row.cost / maxCost) * 100) : 0}%`, background: pricingCategoryColor(row.key) }} /></span>
          <strong className="mb-cat-cost ws-num"><Money value={row.cost} currency={currency} /></strong>
        </li>
      ))}
    </ul>
  );
}

function FreeReplies({ tier }) {
  if (!tier?.numbers?.length) {
    return <p className="ws-muted mb-empty-line">Connect a number to track its free replies.</p>;
  }
  return (
    <div className="mb-free">
      {tier.numbers.map((number) => {
        const used = Math.min(number.used, tier.limit);
        const over = Math.max(0, number.used - tier.limit);
        const pct = Math.min(100, (used / tier.limit) * 100);
        const tone = over > 0 ? 'is-over' : pct >= 80 ? 'is-near' : '';
        return (
          <div key={number.id} className={`mb-free-row ${tone}`}>
            <div className="mb-free-top">
              <span><strong>{number.display_name || 'WhatsApp number'}</strong><small>{number.phone_number}</small></span>
              <b className="ws-num">{formatNumber(number.used)}<small> / {formatNumber(tier.limit)}</small></b>
            </div>
            <span className="mb-meter" role="progressbar" aria-valuemin={0} aria-valuemax={tier.limit} aria-valuenow={used} aria-label={`Free replies used on ${number.display_name || number.phone_number}`}>
              <i style={{ width: `${pct}%` }} />
            </span>
            <small className="mb-free-note">
              {over > 0
                ? `${formatNumber(over)} replies were charged after the free 1,000.`
                : `${formatNumber(number.remaining)} free replies left. Resets on ${monthName(tier.resets_at)}.`}
            </small>
          </div>
        );
      })}
      <div className="mb-free-kinds">
        <span><b>Counts</b>Texts, photos, documents, voice notes and buttons you send in an open chat</span>
        <span><b>Doesn’t count</b>Templates (priced by their kind) and emoji reactions</span>
      </div>
      <p className="wa-help">Counted from replies sent through Auto Appy. Meta’s own count also includes replies sent from other tools on the same number.</p>
    </div>
  );
}

function Numbers({ numbers = [] }) {
  if (!numbers.length) return <p className="ws-muted mb-empty-line">No numbers found for this account.</p>;
  return (
    <ul className="mb-numbers">
      {numbers.map((number) => {
        const quality = qualityOf(number.quality_rating);
        const limit = number.tier_limit;
        const used = number.conversations_last_24h || 0;
        return (
          <li key={number.id}>
            <span className="mb-number-icon" aria-hidden="true"><Smartphone size={16} /></span>
            <span className="ws-grow">
              <strong>{number.display_name || number.phone_number}</strong>
              <small>{number.phone_number}{number.stale ? ' · last known values' : ''}</small>
            </span>
            <span className="mb-number-fact"><small>Quality <HelpTip term="quality" /></small><Status tone={quality.tone}>{quality.label}</Status></span>
            <span className="mb-number-fact">
              <small>Daily limit <HelpTip term="tier" /></small>
              <b className="ws-num">{tierLabel(number.messaging_limit_tier) || '—'}</b>
              {limit ? <em>{formatNumber(used)} used in 24h</em> : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Payment({ payment }) {
  if (!payment) return null;
  const blocked = payment.status === 'action_required';
  const review = payment.status === 'review_pending';
  return (
    <section className={`ws-card ws-card-pad mb-pay${blocked ? ' is-bad' : ''}`}>
      <div className="mb-card-head">
        <span className="ws-icon-tile" aria-hidden="true"><CreditCard size={17} /></span>
        <div className="ws-grow">
          <h2 className="ws-h3">Payment method</h2>
          <p className="ws-muted">{blocked ? 'Meta refused recent messages for a payment reason.' : review ? 'Meta is still reviewing this account.' : 'No payment problems in the last 7 days.'}</p>
        </div>
        <Status tone={blocked ? 'failed' : review ? 'warning' : 'live'}>{blocked ? 'Fix now' : review ? 'In review' : 'Healthy'}</Status>
      </div>
      <p className="mb-pay-text">
        {blocked
          ? 'Add or fix the card in Meta’s Billing Hub. Until then Meta may stop delivering your templates, and replies beyond the free 1,000 a month.'
          : 'From 1 October 2026 a payment method is required for replies beyond the free 1,000 a month. Meta does not tell apps whether a card is saved, so check it in Billing Hub.'}
      </p>
      {blocked && payment.recent_failures?.length > 0 && (
        <ul className="mb-pay-fails">
          {payment.recent_failures.slice(0, 3).map((failure) => <li key={`${failure.at}${failure.message}`}><AlertTriangle size={13} aria-hidden="true" />{failure.message}</li>)}
        </ul>
      )}
      <a className={`ws-btn ${blocked ? 'ws-btn--primary' : 'ws-btn--quiet'} ws-btn--sm`} href={payment.billing_hub_url || BILLING_HUB_URL} target="_blank" rel="noreferrer">Open Billing Hub<ArrowUpRight size={14} /></a>
    </section>
  );
}

function Tiers({ tiers = [] }) {
  if (!tiers.length) return null;
  return (
    <section className="ws-card ws-card-pad">
      <div className="mb-card-head">
        <span className="ws-icon-tile" aria-hidden="true"><Layers size={17} /></span>
        <div className="ws-grow"><h2 className="ws-h3">Volume discounts</h2><p className="ws-muted">Your current price level this month, per country and kind of message.</p></div>
      </div>
      <ul className="mb-tier-list">
        {tiers.map((tier) => (
          <li key={`${tier.country}${tier.category}`}>
            <span><strong>{marketForIso(tier.country)}</strong><small>{pricingCategoryLabel(tier.category)}</small></span>
            <span className="ws-num">{tier.lower === 0 ? 'List price' : 'Discounted'} · up to {tier.upper ? formatNumber(tier.upper) : 'any number of'} messages</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function SpendOverview({ usage, days, setDays, account }) {
  const data = usage.data;
  const pricing = pricingOf(data);
  const currency = account?.currency || null;
  const tier = data?.service_free_tier;
  const freeLeft = tier?.numbers?.reduce((sum, number) => sum + number.remaining, 0);
  const freeTotal = tier ? tier.limit * (tier.numbers?.length || 0) : null;
  const countries = Object.entries(pricing?.by_country || {}).slice(0, 6);
  const updated = usage.savedAt ? relativeTime(new Date(usage.savedAt).toISOString()) : null;

  return (
    <div className="mb-overview">
      <div className="ws-controls">
        <WsSegmented label="Period" items={RANGES} value={days} onChange={setDays} />
        <div className="ws-controls-end">
          <span className="mb-updated" role="status">
            {usage.refreshing ? <><RefreshCw size={13} className="ws-spin" aria-hidden="true" />Checking Meta for new figures…</> : updated ? `Updated ${updated}` : ''}
          </span>
          <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={usage.refresh} disabled={!usage.ready || usage.loading || usage.refreshing}>
            <RefreshCw size={14} />Refresh from Meta
          </button>
        </div>
      </div>

      {usage.error && !data && (
        <section className="ws-card ws-card-pad mb-problem" role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <div><strong>Meta’s figures aren’t available right now</strong><p>{usage.error} You can still read how pricing works and estimate your costs.</p></div>
          <Link className="ws-btn ws-btn--quiet ws-btn--sm" to="/meta-billing/prices">Estimate costs<ArrowRight size={14} /></Link>
        </section>
      )}

      <section className="ws-card ws-kpis mb-kpis" aria-label={`Last ${days} days`}>
        {[
          { label: 'Estimated Meta charges', icon: CreditCard, value: pricing ? <Money value={pricing.estimated_cost} currency={currency} /> : null, hint: pricing?.legacy ? 'Older estimate' : `Last ${days} days` },
          { label: 'Charged messages', icon: Send, value: pricing?.charged_volume !== null && pricing ? formatNumber(pricing.charged_volume) : null, hint: pricing ? `of ${formatNumber(pricing.total_volume)} delivered` : '' },
          { label: 'Free messages', icon: Gift, value: pricing?.free_volume !== null && pricing ? formatNumber(pricing.free_volume) : null, hint: 'Free replies and chats from ads' },
          { label: 'Free replies left', icon: MessageSquareReply, value: freeTotal ? formatNumber(freeLeft) : null, hint: freeTotal ? `of ${formatNumber(freeTotal)} this month` : 'This month' },
        ].map(({ label, icon: Icon, value, hint }) => (
          <div key={label} className="ws-kpi">
            <span className="ws-kpi-label"><Icon size={14} aria-hidden="true" />{label}</span>
            {usage.loading && !data ? <Skeleton width={90} height={26} /> : <span className="mb-kpi-value ws-num">{value ?? '—'}</span>}
            {hint && <span className="ws-kpi-hint">{hint}</span>}
          </div>
        ))}
      </section>

      {data?.errors?.length > 0 && (
        <p className="mb-partial" role="status"><AlertTriangle size={14} aria-hidden="true" />Some figures couldn’t be loaded from Meta: {data.errors.join(' ')}</p>
      )}

      <div className="mb-grid">
        <section className="ws-card ws-card-pad">
          <div className="mb-card-head">
            <span className="ws-icon-tile" aria-hidden="true"><CreditCard size={17} /></span>
            <div className="ws-grow"><h2 className="ws-h3">Where your money went</h2><p className="ws-muted">By kind of message. Only charged messages cost money.</p></div>
            <Link className="ws-btn ws-btn--plain ws-btn--sm" to="/meta-billing/how-it-works?slide=kinds">What are these?</Link>
          </div>
          {usage.loading && !data ? <Skeleton height={140} radius={14} /> : pricing ? <CategoryBreakdown pricing={pricing} currency={currency} /> : <p className="ws-muted mb-empty-line">Meta’s cost report isn’t available for this account yet.</p>}
        </section>

        <section className="ws-card ws-card-pad">
          <div className="mb-card-head">
            <span className="ws-icon-tile" aria-hidden="true"><Gift size={17} /></span>
            <div className="ws-grow"><h2 className="ws-h3">Free replies this month</h2><p className="ws-muted">{formatNumber(SERVICE_FREE_TIER)} per number, every month. Unused ones don’t carry over.</p></div>
          </div>
          {usage.loading && !data ? <Skeleton height={80} radius={14} /> : <FreeReplies tier={tier} />}
        </section>
      </div>

      <div className="mb-grid">
        <section className="ws-card ws-card-pad">
          <div className="mb-card-head">
            <span className="ws-icon-tile" aria-hidden="true"><CalendarClock size={17} /></span>
            <div className="ws-grow"><h2 className="ws-h3">Charges per day</h2><p className="ws-muted">Meta’s estimate, in your billing currency{currency ? ` (${currency})` : ''}.</p></div>
          </div>
          {usage.loading && !data ? <Skeleton height={200} radius={14} /> : <CostChart daily={pricing?.daily || []} currency={currency} />}
        </section>

        <section className="ws-card ws-card-pad">
          <div className="mb-card-head">
            <span className="ws-icon-tile" aria-hidden="true"><Globe2 size={17} /></span>
            <div className="ws-grow"><h2 className="ws-h3">Countries</h2><p className="ws-muted">Price depends on the customer’s country code.</p></div>
          </div>
          {countries.length ? (
            <ul className="mb-countries">
              {countries.map(([iso, row]) => (
                <li key={iso}>
                  <span className="mb-flag" aria-hidden="true">{iso}</span>
                  <span className="ws-grow"><strong>{marketForIso(iso)}</strong><small>{formatNumber(row.volume)} delivered</small></span>
                  <b className="ws-num"><Money value={row.cost} currency={currency} /></b>
                </li>
              ))}
            </ul>
          ) : <p className="ws-muted mb-empty-line">{usage.loading && !data ? 'Loading…' : 'No country data for this period.'}</p>}
        </section>
      </div>

      <div className="mb-grid">
        <section className="ws-card ws-card-pad">
          <div className="mb-card-head">
            <span className="ws-icon-tile" aria-hidden="true"><ShieldCheck size={17} /></span>
            <div className="ws-grow"><h2 className="ws-h3">Your numbers</h2><p className="ws-muted">Quality and how many new people you can message a day.</p></div>
            <Link className="ws-btn ws-btn--plain ws-btn--sm" to="/meta-billing/healthy">How to grow it</Link>
          </div>
          {usage.loading && !data ? <Skeleton height={64} radius={14} /> : <Numbers numbers={data?.phone_numbers} />}
          {data?.account?.business_verification_status && (
            <p className="wa-help mb-verified"><BadgeCheck size={13} aria-hidden="true" />Business verification: {String(data.account.business_verification_status).replace(/_/g, ' ').toLowerCase()}</p>
          )}
        </section>
        <Payment payment={data?.payment} />
      </div>

      <Tiers tiers={pricing?.tiers} />

      <p className="wa-help mb-foot">Figures are Meta’s own estimates and can differ slightly from your invoice. Taxes may be added by Meta. Your actual bill is in Meta’s Billing Hub.</p>
    </div>
  );
}
